import CookieHelper from './cookieHelper';
import columnVisibilityHelper from './columnVisibilityHelper';

// Resizable grid columns. Off by default: a grid opts in with TabOfDataTable's
// resizableColumns, and only then does the header render drag grips. The widths a user
// drags are kept per page in a cookie, exactly like the column selector's hidden columns,
// and keyed the same way (columnVisibilityHelper.getColumnKey) so both features address the
// same column with the same string.
//
// A width is carried into the table as a CSS custom property on the <table> element
// (--cw-<column index>), which the cells reference. That indirection is what makes dragging
// cheap: the grip writes one property on one element, so a report with thousands of unpaged
// rows never re-renders for a resize — not during the drag and not on release either. React
// only writes the var() references into the cells; the values are applied imperatively (the
// grid re-syncs them after each render, which is how a fresh mount and Reset both work).
// Imports are .js/.ts modules typed as `any`; column field objects are untyped, hence `any`.

export interface ColumnWidths {
  [key: string]: number;
}

const COOKIE_PREFIX = 'colw_';

// A column narrower than this is unreadable and one wider than this defeats the point of
// resizing (the neighbouring columns leave the viewport), so the grip clamps to the range.
const MIN_COLUMN_WIDTH = 48;
const MAX_COLUMN_WIDTH = 900;

// The CSS custom property a column's cells read their width from. The index is the position
// in FieldsToShowInDataTable(), which the header and the body both iterate in the same order
// (the same assumption the column selector's index fallback key makes).
function getWidthVariableName(index: number): string {
  return `--cw-${index}`;
}

function getCookieName(type: any): string {
  return COOKIE_PREFIX + columnVisibilityHelper.getPageKey(type);
}

// Read the per-page widths as { columnKey: px }. Anything malformed is dropped rather than
// failing the page — a cookie outlives the column set it was written for.
function readColumnWidths(type: any): ColumnWidths {
  const raw = CookieHelper.getCookie(getCookieName(type), '');
  if (!raw) return {};
  const result: ColumnWidths = {};
  String(raw)
    .split(',')
    .forEach((entry) => {
      const separator = entry.lastIndexOf(':');
      if (separator < 1) return;
      const key = entry.substring(0, separator);
      const width = Number(entry.substring(separator + 1));
      if (!key || !isFinite(width) || width <= 0) return;
      result[key] = width;
    });
  return result;
}

function writeColumnWidths(type: any, widths: ColumnWidths): void {
  const serialized = Object.keys(widths)
    .map((key) => `${key}:${Math.round(widths[key])}`)
    .join(',');
  CookieHelper.setCookie(getCookieName(type), serialized);
}

function clearColumnWidths(type: any): void {
  CookieHelper.setCookie(getCookieName(type), '');
}

// Clamp a dragged width into the readable range.
function clampWidth(width: number): number {
  return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)));
}

// Marks a cell whose value the column width actually cuts. The CSS turns it into the "see the
// whole value" eye, and the grid's click handler reads the value back off the cell's title.
const CLIPPED_CLASS = 'is-clipped';

// Per-column class on the cells, so one column's cells can be found without walking the grid.
function getColumnCellClass(index: number): string {
  return `cw-i${index}`;
}

// Flag the cells of one column that are cut at the current width. Cells are read first and
// written afterwards: interleaving the two forces a layout per cell instead of one for the lot.
// Only cells carrying a title are considered — the title is both the full value the eye shows
// and the marker for "this cell is plain text", so a cell holding a control or a link is left
// alone.
function markClippedCells(table: any, index: number): void {
  if (!table) return;
  const wrappers = table.querySelectorAll(`.${getColumnCellClass(index)}[title]`);
  const clipped: boolean[] = [];
  wrappers.forEach((wrapper: any) => {
    const text = wrapper.firstElementChild;
    clipped.push(!!text && text.scrollWidth > text.clientWidth + 1);
  });
  wrappers.forEach((wrapper: any, position: number) => {
    wrapper.classList.toggle(CLIPPED_CLASS, clipped[position]);
  });
}

// Re-flag every column that is capped — by a width the user dragged or by the field's own
// defaultWidth — plus any that was capped last time round, so a column returned to its natural
// width drops its eyes. An uncapped column is sized to its content and so cannot be cutting
// anything, which is what keeps this off the other ~50 columns of a wide grid.
function markClippedColumns(table: any, fields: any[], widths: ColumnWidths): void {
  if (!table || !Array.isArray(fields)) return;
  const previous: string[] = String(table.dataset.clippedColumns || '')
    .split(',')
    .filter(Boolean);
  const current: string[] = [];
  fields.forEach((field: any, index: number) => {
    const capped =
      (widths && widths[columnVisibilityHelper.getColumnKey(field, index)]) || (field && field.defaultWidth);
    if (capped) current.push(String(index));
  });
  previous.forEach((index: string) => {
    if (current.indexOf(index) < 0) markClippedCells(table, Number(index));
  });
  current.forEach((index: string) => markClippedCells(table, Number(index)));
  table.dataset.clippedColumns = current.join(',');
}

// Put one column's width on the table, or clear it. This is the single write path: the grip
// calls it while dragging and on the double-click reset, and the grid calls it for the whole
// set after a render. Deliberately imperative — React never owns these properties, because
// committing a drag through state would re-render every row (~2s on the unpaged report) to
// reproduce a result the browser has already painted.
function setTableColumnWidth(table: any, index: number, width: number | null): void {
  if (!table || !table.style) return;
  if (width === null) table.style.removeProperty(getWidthVariableName(index));
  else table.style.setProperty(getWidthVariableName(index), `${Math.round(width)}px`);
}

// Sync every column of a table to the stored widths. The fields are walked in render order so
// a column's key resolves to the same index its cells read their variable from. A column with
// no stored width has its property removed, so it falls back to its natural, content-driven
// width — which is what makes the feature invisible until someone drags something, and what
// makes Reset in the Columns flyout take effect.
function applyColumnWidths(table: any, fields: any[], widths: ColumnWidths): void {
  if (!table || !Array.isArray(fields)) return;
  fields.forEach((field: any, index: number) => {
    const width = widths ? widths[columnVisibilityHelper.getColumnKey(field, index)] : null;
    setTableColumnWidth(table, index, width || null);
  });
}

// The inline style a header or body cell's inner wrapper carries in a resizable grid. Both
// halves are needed: the width pins the column once it has been dragged, and the max-width is
// what actually clamps the cell's intrinsic contribution, so the column can shrink below its
// content (a table cell in an auto-layout table grows to its content whatever its own overflow
// says — the cap has to sit on the block wrapper inside it). With no variable set, the
// fallbacks are the browser defaults and the cell behaves exactly as in a normal grid.
// A column may also declare a defaultWidth on its field, which becomes the variable's fallback:
// the column is capped there until the user drags it, and sizes to its content below that. It is
// how a column of free text stays bounded without a character count — one long value in an
// auto-layout table otherwise widens the column past everything else on the page.
function getCellWidthStyle(index: number, defaultWidth: number | undefined): any {
  const variable = `var(${getWidthVariableName(index)}`;
  const fallback = defaultWidth ? `${defaultWidth}px` : null;
  return {
    width: `${variable}, auto)`,
    maxWidth: `${variable}, ${fallback || 'none'})`,
  };
}

const columnResizeHelper = {
  MIN_COLUMN_WIDTH,
  MAX_COLUMN_WIDTH,
  getWidthVariableName,
  readColumnWidths,
  writeColumnWidths,
  clearColumnWidths,
  clampWidth,
  getColumnCellClass,
  markClippedCells,
  markClippedColumns,
  setTableColumnWidth,
  applyColumnWidths,
  getCellWidthStyle,
};

export default columnResizeHelper;
